/**
 * Reusable, presentational users table.
 *
 * Controlled by its parent: it renders the data and surfaces actions, but does
 * not fetch or mutate. Which actions appear is driven by which callbacks are
 * passed — the admin Accounts page passes only `onAddUser` (add-only), while
 * the gamemaster Users page passes all of them (add / edit / deactivate).
 *
 * Improvements here are visible in every place the table is used.
 */

import React from 'react';
import { Table, Button, Badge, Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUserPlus,
    faPen,
    faUserSlash,
    faUserCheck,
} from '@fortawesome/free-solid-svg-icons';
import { User } from '../../types/auth';

export interface UsersTableProps {
    users: User[];
    loading?: boolean;
    /** The signed-in user's id — its row cannot deactivate itself. */
    currentUserId?: string;
    /** User ids with an in-flight mutation (row action shows a spinner). */
    mutatingIds?: string[];
    /** When set, an "Add User" button is rendered in the header. */
    onAddUser?: () => void;
    /** When set, an "Edit" action is rendered per row. */
    onEditUser?: (user: User) => void;
    /** When set, a "Deactivate" / "Reactivate" action is rendered per row. */
    onToggleActive?: (user: User) => void;
    /** Show the Active/Inactive status column (defaults off). */
    showStatus?: boolean;
    /** Optional header label shown to the left of the Add button. */
    title?: string;
    emptyText?: string;
    addLabel?: string;
}

const ROLE_VARIANT: Record<string, string> = {
    admin: 'danger',
    gamemaster: 'primary',
    client: 'secondary',
};

const UsersTable: React.FC<UsersTableProps> = ({
    users,
    loading = false,
    currentUserId,
    mutatingIds = [],
    onAddUser,
    onEditUser,
    onToggleActive,
    showStatus = false,
    title,
    emptyText = 'No users yet.',
    addLabel = 'Add User',
}) => {
    const hasActions = !!onEditUser || !!onToggleActive;
    const showHeader = !!title || !!onAddUser;

    return (
        <>
            {showHeader && (
                <div
                    className={`d-flex align-items-center mb-2 ${
                        title ? 'justify-content-between' : 'justify-content-end'
                    }`}
                >
                    {title && <strong>{title}</strong>}
                    {onAddUser && (
                        <Button variant="outline-primary" size="sm" onClick={onAddUser}>
                            <FontAwesomeIcon icon={faUserPlus} className="me-1" />
                            {addLabel}
                        </Button>
                    )}
                </div>
            )}

            {loading ? (
                <div className="text-center py-3">
                    <Spinner animation="border" size="sm" role="status">
                        <span className="visually-hidden">Loading users...</span>
                    </Spinner>
                </div>
            ) : users.length > 0 ? (
                <Table size="sm" borderless responsive className="mb-0 align-middle">
                    <thead>
                        <tr>
                            <th>Username</th>
                            <th>Email</th>
                            <th>Role</th>
                            {showStatus && <th>Status</th>}
                            {hasActions && <th className="text-end">Actions</th>}
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((user) => {
                            const isSelf = user.id === currentUserId;
                            const isMutating = mutatingIds.includes(user.id);
                            return (
                                <tr key={user.id}>
                                    <td>{user.username}</td>
                                    <td>{user.email}</td>
                                    <td>
                                        <Badge
                                            bg={ROLE_VARIANT[user.role] ?? 'secondary'}
                                        >
                                            {user.role}
                                        </Badge>
                                    </td>
                                    {showStatus && (
                                        <td>
                                            <Badge
                                                bg={user.is_active ? 'success' : 'secondary'}
                                            >
                                                {user.is_active ? 'Active' : 'Inactive'}
                                            </Badge>
                                        </td>
                                    )}
                                    {hasActions && (
                                        <td className="text-end">
                                            {onEditUser && (
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    className="me-2"
                                                    onClick={() => onEditUser(user)}
                                                    disabled={isMutating}
                                                    title="Edit user"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faPen}
                                                        className="me-1"
                                                    />
                                                    Edit
                                                </Button>
                                            )}
                                            {onToggleActive && (
                                                <Button
                                                    variant={
                                                        user.is_active
                                                            ? 'outline-danger'
                                                            : 'outline-success'
                                                    }
                                                    size="sm"
                                                    onClick={() => onToggleActive(user)}
                                                    disabled={isMutating || isSelf}
                                                    title={
                                                        isSelf
                                                            ? 'You cannot deactivate your own account'
                                                            : user.is_active
                                                            ? 'Deactivate user'
                                                            : 'Reactivate user'
                                                    }
                                                >
                                                    {isMutating ? (
                                                        <Spinner
                                                            as="span"
                                                            animation="border"
                                                            size="sm"
                                                            role="status"
                                                            aria-hidden="true"
                                                        />
                                                    ) : (
                                                        <>
                                                            <FontAwesomeIcon
                                                                icon={
                                                                    user.is_active
                                                                        ? faUserSlash
                                                                        : faUserCheck
                                                                }
                                                                className="me-1"
                                                            />
                                                            {user.is_active
                                                                ? 'Deactivate'
                                                                : 'Reactivate'}
                                                        </>
                                                    )}
                                                </Button>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                    </tbody>
                </Table>
            ) : (
                <p className="text-muted mb-0">{emptyText}</p>
            )}
        </>
    );
};

export default UsersTable;
